import React, { useMemo } from 'react';

import moment from 'moment';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { RouteOptimizationResult } from '@bringg/dashboard-sdk/dist/RouteOptimization/RouteOptimization.consts';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';

import InProgressStep from 'bringg-web/features/optimization-wizard/in-progress-step/in-progress-step';
import useUserAssignerStore from '../use-user-assigner-store';
import { OptimizationItemType } from 'bringg-web/features/routes-planner/models';

import styles from './optimization-view.module.scss';

export enum OptimizationStatus {
	NONE = 0,
	IN_PROGRESS = 1,
	DONE_SUCCESS = 2,
	FAILED = 3
}

interface Props {
	optimizationTime: { startTime: Date; endTime: Date };
}
const OptimizationView = ({ optimizationTime }: Props) => {
	const { t } = useTranslation();

	const { optimizationProgressPercentage, sentToOptimizationCount, optimizationStatus, optimizationResult } =
		useUserAssignerStore();

	const tasksStatusAfterOptimization = useMemo(() => {
		let assign = 0;
		let unassign = 0;

		if ((optimizationResult as RouteOptimizationResult)?.data) {
			(optimizationResult as RouteOptimizationResult).data.forEach(data => {
				if (data.type === OptimizationItemType.TASK) {
					assign++;
				} else if (data.type === 'unassigned_task') {
					unassign++;
				}
			});
		}
		return { assign, unassign };
	}, [optimizationResult]);

	const getTaskStatusData = (count: number, iconName: BringgFontIcons, iconClassName: string, text: string) => {
		return count ? (
			<div>
				<BringgIcon iconName={iconName} className={iconClassName} />
				<span className={styles.number}>{count}</span>
				<span className={styles.numberText}>{t(text, { count })}</span>
			</div>
		) : null;
	};
	return (
		<div>
			<InProgressStep
				progressPercent={optimizationProgressPercentage}
				tasksCountInTimeRange={sentToOptimizationCount.tasks}
				optimizationTime={{
					startTime: moment(optimizationTime.startTime),
					endTime: moment(optimizationTime.endTime)
				}}
				selectedResourcesCount={sentToOptimizationCount.resources}
				isMultiDayOptimization={false}
				barStatus={optimizationStatus === OptimizationStatus.FAILED ? 'exception' : 'normal'}
			/>
			<div className={styles.tasksSummary}>
				<div>
					{getTaskStatusData(
						tasksStatusAfterOptimization.assign,
						BringgFontIcons.Suffix,
						styles.assignIcon,
						'RUN_USER_ASSIGNMENT.TASKS_ASSIGNED_TO_ROUTE'
					)}
				</div>
				<div>
					{getTaskStatusData(
						tasksStatusAfterOptimization.unassign,
						BringgFontIcons.CloseCircle,
						styles.unassignIcon,
						'RUN_USER_ASSIGNMENT.TASKS_UNASSIGNED_TO_ROUTE'
					)}
				</div>
			</div>
		</div>
	);
};

export default observer(OptimizationView);
